
import React from 'react';
import { Check } from 'lucide-react';
import OnboardingLayout from '../../OnboardingLayout';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';

interface ExpertiseSelectionProps {
  formData: any;
  updateFormData: (data: any) => void;
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrevious: () => void;
}

const popularTopics = [
  'Politics', 'Technology', 'Climate', 'Business', 'Health',
  'Education', 'Science', 'Arts', 'Sports', 'International Affairs',
  'Local News', 'Finance', 'Economy', 'Social Justice', 'Environment',
  'Data Journalism', 'Investigative', 'Culture', 'Opinion', 'Lifestyle'
];

const ExpertiseSelection: React.FC<ExpertiseSelectionProps> = ({
  formData,
  updateFormData,
  currentStep,
  totalSteps,
  onNext,
  onPrevious
}) => {
  const [selectedTopics, setSelectedTopics] = React.useState<string[]>(formData.topics || []);
  const [customTopic, setCustomTopic] = React.useState('');

  const handleTopicSelect = (topic: string) => {
    if (selectedTopics.includes(topic)) {
      setSelectedTopics(selectedTopics.filter(t => t !== topic));
    } else {
      setSelectedTopics([...selectedTopics, topic]);
    }
  };

  const handleAddCustomTopic = () => {
    if (customTopic.trim() && !selectedTopics.includes(customTopic.trim())) {
      setSelectedTopics([...selectedTopics, customTopic.trim()]);
      setCustomTopic('');
    }
  };

  const handleNext = () => {
    updateFormData({ topics: selectedTopics });
    onNext();
  };

  return (
    <OnboardingLayout
      title="Select Your Expertise Areas"
      subtitle="Choose topics you specialize in to help readers find your content"
      currentStep={currentStep}
      totalSteps={totalSteps}
      onNext={handleNext}
      onPrevious={onPrevious}
      nextDisabled={selectedTopics.length === 0}
    >
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <h3 className="text-lg font-medium mb-4">Popular Topics</h3>
          <div className="flex flex-wrap gap-2">
            {popularTopics.map((topic) => (
              <Badge
                key={topic}
                variant={selectedTopics.includes(topic) ? "default" : "outline"}
                className={`px-3 py-1 cursor-pointer ${
                  selectedTopics.includes(topic) 
                    ? "bg-primary text-primary-foreground" 
                    : "hover:bg-muted"
                }`}
                onClick={() => handleTopicSelect(topic)}
              >
                {selectedTopics.includes(topic) && (
                  <Check className="mr-1 h-3 w-3" />
                )}
                {topic}
              </Badge>
            ))}
          </div>
        </div>
        
        <div className="mb-8">
          <h3 className="text-lg font-medium mb-4">Add Custom Topics</h3>
          <div className="flex">
            <Input
              value={customTopic}
              onChange={(e) => setCustomTopic(e.target.value)}
              placeholder="e.g., Blockchain, Urban Planning, Marine Biology"
              className="mr-2"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddCustomTopic();
                }
              }}
            />
            <button
              onClick={handleAddCustomTopic}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md"
              disabled={!customTopic.trim()}
            >
              Add
            </button>
          </div>
        </div>
        
        {selectedTopics.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-medium mb-4">Your Selected Topics</h3>
            <div className="flex flex-wrap gap-2">
              {selectedTopics.map((topic) => (
                <Badge key={topic} className="px-3 py-1 bg-primary text-primary-foreground">
                  {topic}
                  <span 
                    className="ml-1 cursor-pointer" 
                    onClick={() => handleTopicSelect(topic)}
                  >
                    ✕
                  </span>
                </Badge>
              ))}
            </div>
          </div>
        )}
        
        <div className="bg-muted/30 rounded-lg p-4">
          <h4 className="text-sm font-medium mb-2">Why your expertise matters:</h4>
          <p className="text-sm text-muted-foreground mb-4">
            Selecting your areas of expertise helps readers find your content when they're looking for 
            specific topics. It also helps us recommend your articles to interested readers.
          </p>
          <p className="text-sm text-muted-foreground">
            We recommend selecting between 3-7 topics that best represent your core expertise areas. 
            You can always update these later.
          </p>
        </div>
      </div>
    </OnboardingLayout>
  );
};

export default ExpertiseSelection;
